/**
 * Domain selection step - shows all parsed SDS forms in a table with checkboxes. User selects which
 * forms to create as DTAs.
 */

import Badge from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Header from "@cloudscape-design/components/header"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Table, { type TableProps } from "@cloudscape-design/components/table"

import type { ParseSpecResponse } from "@/generated/sdk"
import type { ParsedFormResult } from "./types"

// Derived from the SDK `ParsedFormResult`. Counts and `document_content` are
// optional because the reupload flow supplies a partial form list without them.
type DomainSelectionForm = Pick<ParsedFormResult, "name" | "label"> &
  Partial<
    Pick<
      ParsedFormResult,
      | "domain"
      | "field_count"
      | "codelist_count"
      | "is_repeating"
      | "rule_count"
      | "cross_form_rule_count"
      | "max_repeats"
      | "document_content"
    >
  >

interface DomainSelectionStepProps {
  parseResult: {
    forms?: DomainSelectionForm[]
  } & Partial<
    Pick<ParseSpecResponse, "parser_label" | "total_forms" | "total_codelists" | "total_rules">
  >
  /**
   * Set of form names currently checked for creation.
   */
  selectedForms: Set<string>
  onToggleForm: (formName: string) => void
  onToggleAll: () => void
  /**
   * Callback when the user clicks the submit button.
   */
  onSubmit: () => void
  onClose: () => void
  /**
   * Selection mode: "multi" (checkboxes, default) or "single" (radio buttons).
   */
  mode?: "multi" | "single"
  /**
   * Custom submit button label. Defaults to "Create N DTAs".
   */
  submitLabel?: string
  /**
   * Whether the submit action is loading.
   */
  loading?: boolean
  /**
   * Domain name to highlight as "(current)".
   */
  currentDomain?: string
  /**
   * Hide the EDC-specific "Repeating" and "Rules" columns (and the rules count in the summary
   * line). SDTM mapping specs carry no edit-check rules and no repeating-form concept, so those
   * columns would always show 0/No.
   */
  hideSpecColumns?: boolean
  /**
   * When provided, renders a "Back" button (used by the upload modal to return to the file-upload
   * step). Omitted in contexts with no previous step, e.g. the reupload flow, where no Back button
   * is shown.
   */
  onBack?: () => void
}

export default function DomainSelectionStep({
  parseResult,
  selectedForms,
  onToggleForm,
  onToggleAll,
  onSubmit,
  onClose,
  mode = "multi",
  submitLabel,
  loading = false,
  currentDomain,
  hideSpecColumns = false,
  onBack,
}: DomainSelectionStepProps) {
  const forms = parseResult.forms ?? []
  const isMulti = mode === "multi"

  // Presentation sort (the UI owns display order): group a domain's datasets
  // together, primary dataset first, then Supplemental (SUPP*) then cross-ref
  // (XREF*). Falls back to the form name when no domain is present (EDC forms).
  const datasetKindRank = (name: string) => (/^SUPP/i.test(name) ? 1 : /^XREF/i.test(name) ? 2 : 0)
  const sortedForms = [...forms].sort((a, b) => {
    const domA = a.domain ?? a.name
    const domB = b.domain ?? b.name
    if (domA !== domB) return domA.localeCompare(domB)
    const rankDelta = datasetKindRank(a.name) - datasetKindRank(b.name)
    if (rankDelta !== 0) return rankDelta
    return a.name.localeCompare(b.name)
  })
  const showDomainColumn = forms.some((f) => f.domain)

  const selectedItems = sortedForms.filter((f) => selectedForms.has(f.name))

  const handleSelectionChange: TableProps["onSelectionChange"] = (event) => {
    const newSelected = new Set(
      (event.detail.selectedItems as DomainSelectionForm[]).map((item) => item.name),
    )

    if (isMulti) {
      // Determine if this is a select-all/deselect-all or single toggle
      const prevSize = selectedForms.size
      const newSize = newSelected.size
      const sizeDelta = Math.abs(newSize - prevSize)

      if (
        sizeDelta > 1 ||
        (newSize === forms.length && prevSize !== forms.length) ||
        newSize === 0
      ) {
        // select-all or deselect-all
        onToggleAll()
      } else {
        // Find the toggled item
        const added = [...newSelected].find((name) => !selectedForms.has(name))
        const removed = [...selectedForms].find((name) => !newSelected.has(name))
        const toggled = added || removed
        if (toggled) onToggleForm(toggled)
      }
    } else {
      // Single mode: find the newly selected item
      const newItem = (event.detail.selectedItems as DomainSelectionForm[])[0]
      if (newItem) onToggleForm(newItem.name)
    }
  }

  const columnDefinitions: TableProps<DomainSelectionForm>["columnDefinitions"] = [
    {
      id: "name",
      header: "Form",
      cell: (item) => (
        <SpaceBetween direction="horizontal" size="xs">
          <span>{item.name}</span>
          {currentDomain && (currentDomain === item.name || currentDomain === item.label) && (
            <Badge color="grey">(current)</Badge>
          )}
        </SpaceBetween>
      ),
    },
    {
      id: "label",
      header: "Label",
      cell: (item) => item.label,
    },
    ...(showDomainColumn
      ? [
          {
            id: "domain",
            header: "Domain",
            cell: (item: DomainSelectionForm) => item.domain ?? "-",
          },
        ]
      : []),
    {
      id: "fields",
      header: "Fields",
      cell: (item) => item.field_count,
    },
    {
      id: "codelists",
      header: "Codelists",
      cell: (item) => item.codelist_count,
    },
    ...(hideSpecColumns
      ? []
      : [
          {
            id: "repeating",
            header: "Repeating",
            cell: (item: DomainSelectionForm) =>
              item.is_repeating
                ? item.max_repeats
                  ? `Yes (${item.max_repeats.toLocaleString()})`
                  : "Yes"
                : "No",
          },
        ]),
    ...(isMulti && !hideSpecColumns
      ? [
          {
            id: "rules",
            header: "Rules",
            cell: (item: DomainSelectionForm) => item.rule_count,
          },
        ]
      : []),
  ]

  const defaultSubmitLabel = `Create ${selectedForms.size} DTA${selectedForms.size !== 1 ? "s" : ""}`

  return (
    <SpaceBetween size="l">
      <SpaceBetween size="xs">
        <Header variant="h2">{isMulti ? "Select Domains" : "Select Domain"}</Header>
        <Box variant="p" color="text-body-secondary">
          {isMulti
            ? `The ${parseResult.parser_label || "EDC specification"} was parsed successfully. Select the forms you want to create as Data Transfer Agreements. Each selected form will become a separate Data Transfer Agreement with its fields, codelists${hideSpecColumns ? "" : ", and validation rules"} pre-populated.`
            : "Select the domain to import. This will replace all existing content."}
        </Box>
        {isMulti && (
          <Box variant="p" fontWeight="bold">
            {parseResult.total_forms} forms · {parseResult.total_codelists} codelists
            {!hideSpecColumns && ` · ${parseResult.total_rules} rules`}
          </Box>
        )}
        {isMulti && (
          <Box variant="p" color="text-body-secondary">
            {selectedForms.size} of {forms.length} selected
          </Box>
        )}
        {isMulti && showDomainColumn && (
          <Box variant="p" color="text-body-secondary">
            Primary domains are pre-selected. Supplemental (SUPP), cross-reference (XREF), and
            trial-design datasets are unselected by default.
          </Box>
        )}
      </SpaceBetween>
      {/* Bound the table's scroll region so the modal stays a fixed size and the footer
          buttons remain visible regardless of form count. stickyHeader pins column headers. */}
      <div style={{ maxHeight: 400, overflowY: "auto" }}>
        <Table
          variant="embedded"
          trackBy="name"
          selectionType={isMulti ? "multi" : "single"}
          selectedItems={selectedItems}
          onSelectionChange={handleSelectionChange}
          columnDefinitions={columnDefinitions}
          items={sortedForms}
          stickyHeader
        />
      </div>
      <Box float="right">
        <SpaceBetween direction="horizontal" size="xs">
          {/* The modal flow provides onBack (return to file-upload) and closes
              via the header X. The reupload flow has no Back and no X, so it
              falls back to a Cancel. */}
          {onBack ? (
            <Button variant="link" onClick={onBack}>
              Back
            </Button>
          ) : (
            <Button variant="normal" onClick={onClose}>
              Cancel
            </Button>
          )}
          <Button
            variant="primary"
            onClick={onSubmit}
            disabled={selectedForms.size === 0 || loading}
            loading={loading}
          >
            {loading ? "Processing..." : submitLabel || defaultSubmitLabel}
          </Button>
        </SpaceBetween>
      </Box>
    </SpaceBetween>
  )
}
